import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Polling: 2s intervals × 23 attempts ≈ 46s window. Stripe webhook delivery
// typically lands inside 5-10s but occasional delivery delays push past 30s,
// at which point we switch to a soft "still processing" message rather than
// an alarming "trouble" one — the customer's confirmation email + admin Lead
// both arrive on their own via the webhook (or our /status polling safety net
// on a later page load).
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 23;

export default function ThankYou() {
  const [params] = useSearchParams();
  const session_id = params.get('session_id');
  // 'loading' | 'paid' | 'unpaid' | 'expired' | 'error' | 'missing'
  const [phase, setPhase] = useState(session_id ? 'loading' : 'missing');
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!session_id) return;
    let alive = true;
    let timer = null;

    const poll = async () => {
      if (!alive) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${session_id}`);
        if (!alive) return;
        setInfo(data);
        const ps = (data?.payment_status || '').toLowerCase();
        const st = (data?.status || '').toLowerCase();
        if (ps === 'paid') { setPhase('paid'); return; }
        if (st === 'expired') { setPhase('expired'); return; }
        if (attempts.current >= MAX_ATTEMPTS) {
          // We give up polling — webhook should still process this server-side
          setPhase('unpaid');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!alive) return;
        if (attempts.current >= MAX_ATTEMPTS) { setPhase('error'); return; }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [session_id]);

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO title="Thank you" path="/thank-you" description="Thank you for your purchase. Your Heirloom Stories session is confirmed." />
      <Navbar />
      <section className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-2xl mx-auto px-6 sm:px-12 text-center" data-testid="thank-you-page">

          {phase === 'loading' && (
            <div className="animate-fade-in-up" data-testid="thank-you-loading">
              <div className="w-14 h-14 border border-[#A89F91] flex items-center justify-center mx-auto mb-8">
                <Loader2 size={22} className="text-[#A89F91] animate-spin" />
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
                Confirming your payment…
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed max-w-md mx-auto">
                Just a moment while we confirm the transaction with Stripe. This typically takes a few seconds.
              </p>
            </div>
          )}

          {phase === 'paid' && (
            <div className="animate-fade-in-up" data-testid="thank-you-paid">
              <div className="w-14 h-14 border border-[#A89F91] flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={26} className="text-[#A89F91]" strokeWidth={1.5} />
              </div>
              <p className="overline mb-4">Payment Received</p>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight mb-6">
                Thank you.
              </h1>
              <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-lg mx-auto mb-8">
                Your purchase is confirmed. One of our interviewers will reach out within one business day to schedule your discovery call.
              </p>
              {info?.amount_total != null && (
                <div className="inline-flex items-center gap-3 border border-[#E7E5DF] bg-white px-5 py-3 mb-12" data-testid="thank-you-amount">
                  <span className="text-xs uppercase tracking-widest text-[#A89F91]">Amount paid</span>
                  <span className="font-['Cormorant_Garamond'] text-xl text-[#1C1917] tabular-nums">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: (info.currency || 'usd').toUpperCase(),
                      maximumFractionDigits: 2,
                    }).format(info.amount_total / 100)}
                  </span>
                </div>
              )}
              <div className="w-16 h-[1px] bg-[#D6D3CD] mx-auto mb-12" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-xl mx-auto mb-16">
                <div><span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">01</span><p className="text-sm text-[#57534E]">A confirmation email is on its way</p></div>
                <div><span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">02</span><p className="text-sm text-[#57534E]">An interviewer will reach out within one business day</p></div>
                <div><span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">03</span><p className="text-sm text-[#57534E]">We&apos;ll schedule your discovery call together</p></div>
              </div>
              <Link to="/sample" data-testid="thank-you-sample-link" className="inline-flex items-center gap-2 text-sm text-[#57534E] border border-[#D6D3CD] hover:border-[#A89F91] hover:text-[#1C1917] px-6 py-3 transition-all duration-300">
                See a Sample Heirloom <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {phase === 'unpaid' && (
            <div className="animate-fade-in-up" data-testid="thank-you-unpaid">
              <div className="w-14 h-14 border border-[#A89F91] flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={22} className="text-[#A89F91]" strokeWidth={1.5} />
              </div>
              <p className="overline mb-4">Almost there</p>
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-tight mb-6">
                Your payment is being processed.
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed max-w-lg mx-auto mb-10">
                You should receive a confirmation email within a few minutes. If you don&apos;t, please email us at <a className="underline" href="mailto:hello@heirloomstorystudios.com">hello@heirloomstorystudios.com</a> and we&apos;ll sort it out.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 border border-[#D6D3CD] hover:border-[#A89F91] text-[#1C1917] px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300">
                Return home <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {phase === 'expired' && (
            <div className="animate-fade-in-up" data-testid="thank-you-expired">
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-tight mb-6">
                Checkout session expired
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed max-w-lg mx-auto mb-8">
                Your checkout session timed out before payment was completed. You can start a new checkout below.
              </p>
              <Link to="/contact?tab=buy" className="inline-flex items-center gap-2 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300">
                Back to packages <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {phase === 'error' && (
            <div className="animate-fade-in-up" data-testid="thank-you-error">
              <div className="w-14 h-14 border border-[#A89F91] flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={22} className="text-[#A89F91]" strokeWidth={1.5} />
              </div>
              <p className="overline mb-4">Almost there</p>
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-tight mb-6">
                Your payment is being processed.
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed max-w-lg mx-auto mb-10">
                You should receive a confirmation email within a few minutes. If you don&apos;t, please email us at <a className="underline" href="mailto:hello@heirloomstorystudios.com">hello@heirloomstorystudios.com</a> and we&apos;ll sort it out.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 border border-[#D6D3CD] hover:border-[#A89F91] text-[#1C1917] px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300">
                Return home <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {phase === 'missing' && (
            <div data-testid="thank-you-missing">
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-tight mb-6">
                Page not available
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed max-w-lg mx-auto mb-8">
                This page is only reachable after completing a purchase. To begin, head to our packages.
              </p>
              <Link to="/contact?tab=buy" className="inline-flex items-center gap-2 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300">
                Browse packages <ArrowRight size={14} />
              </Link>
            </div>
          )}

        </div>
      </section>
      <Footer />
    </div>
  );
}
